import { PointerEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CloseOutlined, MessageOutlined, SearchOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { Button, Empty, Input, List, Popover, Spin, Typography } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "../../../../stores/Store";
import { CommonAvatar } from "../avatar/CommonAvatar";
import { CommonBadge } from "../badge/CommonBadge";
import { useChatConversations, useChatSocket, useMarkChatRead } from "../../../../hooks/useChat";
import { useChatGroupSocket, useMarkGroupRead, useMyGroups } from "../../../../hooks/useChatGroup";
import { useOnlineUsers, usePresenceSocket } from "../../../../hooks/usePresence";
import { ChatConversationDto } from "../../../../models/chat/ChatConversationDto";
import { ChatGroupListDto } from "../../../../models/chat/ChatGroup";
import { getStickerEmoji } from "../../../../utils/chatStickers";
import {
  CHAT_CONVERSATION_OPEN_EVENT,
  CHAT_CONVERSATION_READ_EVENT,
  CHAT_DOCK_OPEN_EVENT,
  CHAT_GROUP_OPEN_EVENT,
  CHAT_GROUP_READ_EVENT,
  ChatConversationOpenDetail,
  ChatConversationReadDetail,
  ChatGroupOpenDetail,
  ChatGroupReadDetail,
} from "./ChatEvents";
import { DirectChatWindow } from "./DirectChatWindow";
import { GroupChatWindow } from "./GroupChatWindow";
import { CreateGroupModal } from "./CreateGroupModal";
import { getProfileName, useChatUserProfiles } from "./useChatUserProfiles";
import { useLocation } from "react-router-dom";
import { MESSAGES_PATHS } from "../../../../constances/Paths.constance";
import { employeeService } from "../../../hrm/services/EmployeeService";

const MAX_VISIBLE_CHAT_BUBBLES: number = 4;
const BUBBLE_MARGIN: number = 8;
const CHAT_STACK_POS_KEY: string = "adc_chat_stack_pos_v2";
const FLOATING_CHAT_DOCK_STATE_KEY: string = "adc_chat_dock_state_v1";
const CHAT_STACK_WIDTH: number = 430;
const CHAT_STACK_HEIGHT: number = 640;
const COLLAPSED_STACK_SIZE: number = 64;
const CLOSE_ANIMATION_MS: number = 220;
const CHAT_WINDOW_ANIMATION_MS: number = 240;

type OpenChat =
  | { kind: "direct"; minimized: boolean; partnerUserId: string; partnerName?: string }
  | { kind: "group"; minimized: boolean; groupId: string; groupName?: string; avatarUrl?: string | null };

const chatKey = (c: OpenChat) => (c.kind === "direct" ? `d:${c.partnerUserId}` : `g:${c.groupId}`);

type UnifiedItem =
  | { key: string; kind: "direct"; sortMs: number; conv: ChatConversationDto }
  | { key: string; kind: "group"; sortMs: number; group: ChatGroupListDto };

interface FloatingChatState {
  activeChatKey: string | null;
  openChats: OpenChat[];
}

type SnapEdge = "left" | "right" | "top" | "bottom";

interface BubblePos {
  x: number;
  y: number;
  edge?: SnapEdge;
  edgeOffset?: number;
}

type DragMode = "stack" | "avatar";
type DragPreviewPhase = "dragging" | "closing" | "returning";
type ChatWindowMotionPhase = "entering" | "open" | "exiting";

interface DragPreview {
  key: string;
  x: number;
  y: number;
  returnX: number;
  returnY: number;
  name: string;
  src?: string | null;
  kind: OpenChat["kind"];
  unread: number;
  online: boolean;
  phase: DragPreviewPhase;
}

interface ChatWindowMotion {
  key: string;
  chat: OpenChat;
  phase: ChatWindowMotionPhase;
}

const clamp01 = (value: number) => Math.min(Math.max(value, 0), 1);

const clampChatStackPos = (x: number, y: number, width = CHAT_STACK_WIDTH, height = CHAT_STACK_HEIGHT): BubblePos => ({
  x: Math.min(Math.max(x, BUBBLE_MARGIN), Math.max(BUBBLE_MARGIN, window.innerWidth - width - BUBBLE_MARGIN)),
  y: Math.min(Math.max(y, BUBBLE_MARGIN), Math.max(BUBBLE_MARGIN, window.innerHeight - height - BUBBLE_MARGIN)),
});

const getNearestChatStackEdge = (x: number, y: number, width = CHAT_STACK_WIDTH, height = CHAT_STACK_HEIGHT): SnapEdge => {
  const clamped = clampChatStackPos(x, y, width, height);
  const edges = [
    { edge: "left" as const, distance: Math.abs(clamped.x - BUBBLE_MARGIN) },
    { edge: "right" as const, distance: Math.abs(window.innerWidth - BUBBLE_MARGIN - (clamped.x + width)) },
    { edge: "top" as const, distance: Math.abs(clamped.y - BUBBLE_MARGIN) },
    { edge: "bottom" as const, distance: Math.abs(window.innerHeight - BUBBLE_MARGIN - (clamped.y + height)) },
  ].sort((a, b) => a.distance - b.distance);
  return edges[0].edge;
};

const getChatStackEdgeOffset = (pos: BubblePos, edge: SnapEdge, width = CHAT_STACK_WIDTH, height = CHAT_STACK_HEIGHT) => {
  const clamped = clampChatStackPos(pos.x, pos.y, width, height);
  if (edge === "left" || edge === "right") {
    const track = Math.max(1, window.innerHeight - BUBBLE_MARGIN * 2);
    return clamp01((clamped.y + height / 2 - BUBBLE_MARGIN) / track);
  }

  const track = Math.max(1, window.innerWidth - BUBBLE_MARGIN * 2);
  return clamp01((clamped.x + width / 2 - BUBBLE_MARGIN) / track);
};

const alignChatStackToEdge = (pos: BubblePos, edge: SnapEdge, width = CHAT_STACK_WIDTH, height = CHAT_STACK_HEIGHT): BubblePos => {
  const edgeOffset = typeof pos.edgeOffset === "number" ? clamp01(pos.edgeOffset) : getChatStackEdgeOffset(pos, edge, width, height);

  if (edge === "left" || edge === "right") {
    const track = Math.max(1, window.innerHeight - BUBBLE_MARGIN * 2);
    const x = edge === "left" ? BUBBLE_MARGIN : window.innerWidth - width - BUBBLE_MARGIN;
    const y = BUBBLE_MARGIN + track * edgeOffset - height / 2;
    return { ...clampChatStackPos(x, y, width, height), edge, edgeOffset };
  }

  const track = Math.max(1, window.innerWidth - BUBBLE_MARGIN * 2);
  const x = BUBBLE_MARGIN + track * edgeOffset - width / 2;
  const y = edge === "top" ? BUBBLE_MARGIN : window.innerHeight - height - BUBBLE_MARGIN;
  return { ...clampChatStackPos(x, y, width, height), edge, edgeOffset };
};

const snapChatStackPos = (x: number, y: number, width = CHAT_STACK_WIDTH, height = CHAT_STACK_HEIGHT): BubblePos => {
  const edge = getNearestChatStackEdge(x, y, width, height);
  const clamped = clampChatStackPos(x, y, width, height);
  return alignChatStackToEdge({ ...clamped, edgeOffset: getChatStackEdgeOffset(clamped, edge, width, height) }, edge, width, height);
};

const samePos = (a: BubblePos | null, b: BubblePos) =>
  !!a &&
  Math.round(a.x) === Math.round(b.x) &&
  Math.round(a.y) === Math.round(b.y) &&
  a.edge === b.edge &&
  (a.edgeOffset === b.edgeOffset ||
    (typeof a.edgeOffset === "number" && typeof b.edgeOffset === "number" && Math.abs(a.edgeOffset - b.edgeOffset) < 0.001));

const getDefaultChatStackPos = (): BubblePos =>
  clampChatStackPos((window.innerWidth - CHAT_STACK_WIDTH) / 2, Math.min(96, Math.max(24, window.innerHeight * 0.12)));

const getFloatingChatStateKey = (userId?: string | null) => `${FLOATING_CHAT_DOCK_STATE_KEY}:${userId || "anonymous"}`;

const makeVisibleChat = (chat: OpenChat): OpenChat => (chat.kind === "direct" ? { ...chat, minimized: false } : { ...chat, minimized: false });

const isOpenChat = (value: unknown): value is OpenChat => {
  if (!value || typeof value !== "object") return false;
  const chat = value as Partial<OpenChat>;
  if (typeof chat.minimized !== "boolean") return false;
  if (chat.kind === "direct") return typeof chat.partnerUserId === "string" && chat.partnerUserId.length > 0;
  if (chat.kind === "group") return typeof chat.groupId === "string" && chat.groupId.length > 0;
  return false;
};

const normalizeFloatingChatState = (state: FloatingChatState): FloatingChatState => {
  const unique = new Map<string, OpenChat>();
  state.openChats.forEach((chat) => unique.set(chatKey(chat), chat));
  const openChats = Array.from(unique.values());
  const activeChatKey = state.activeChatKey && openChats.some((chat) => chatKey(chat) === state.activeChatKey) ? state.activeChatKey : null;
  return { activeChatKey, openChats };
};

const loadFloatingChatState = (userId?: string | null): FloatingChatState => {
  try {
    const raw = localStorage.getItem(getFloatingChatStateKey(userId));
    if (!raw) return { activeChatKey: null, openChats: [] };
    const parsed = JSON.parse(raw);
    const openChats = Array.isArray(parsed?.openChats) ? parsed.openChats.filter(isOpenChat) : [];
    const activeChatKey = typeof parsed?.activeChatKey === "string" ? parsed.activeChatKey : null;
    return normalizeFloatingChatState({ activeChatKey, openChats });
  } catch {
    return { activeChatKey: null, openChats: [] };
  }
};

const loadChatStackPos = (): BubblePos | null => {
  try {
    const raw = localStorage.getItem(CHAT_STACK_POS_KEY);
    if (!raw) return null;
    const pos = JSON.parse(raw);
    if (typeof pos?.x !== "number" || typeof pos?.y !== "number") return null;
    const edge = ["left", "right", "top", "bottom"].includes(pos?.edge) ? (pos.edge as SnapEdge) : undefined;
    const edgeOffset = typeof pos?.edgeOffset === "number" ? clamp01(pos.edgeOffset) : undefined;
    const clamped = { ...clampChatStackPos(pos.x, pos.y), edgeOffset };
    return edge ? alignChatStackToEdge(clamped, edge) : clamped;
  } catch {
    return null;
  }
};

export const FloatingChat = () => {
  const { userId } = useSelector((state: RootState) => state.auth);
  const enabled = !!userId;
  const initialDockState = useRef<FloatingChatState>(loadFloatingChatState(userId));
  const hydratedUserRef = useRef<string | null>(userId ?? null);

  const [panelOpen, setPanelOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [openChats, setOpenChats] = useState<OpenChat[]>(initialDockState.current.openChats);
  const [activeChatKey, setActiveChatKey] = useState<string | null>(initialDockState.current.activeChatKey);
  const [createOpen, setCreateOpen] = useState(false);
  const [localUnreadByChatKey, setLocalUnreadByChatKey] = useState<Record<string, number>>({});
  const openChatsRef = useRef<OpenChat[]>(initialDockState.current.openChats);
  const activeChatKeyRef = useRef<string | null>(initialDockState.current.activeChatKey);

  const [chatStackPos, setChatStackPos] = useState<BubblePos | null>(loadChatStackPos);
  const [draggingChatKey, setDraggingChatKey] = useState<string | null>(null);
  const [dragStarted, setDragStarted] = useState(false);
  const [dragOverClose, setDragOverClose] = useState(false);
  const [closingDrop, setClosingDrop] = useState<{ key: string; all: boolean } | null>(null);
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const [chatWindowMotion, setChatWindowMotion] = useState<ChatWindowMotion | null>(null);
  const chatDragRef = useRef<{
    key: string;
    mode: DragMode;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
    closeAllOnDrop?: boolean;
    wasOpenOnDrag?: boolean;
    savedStackPos?: BubblePos | null;
    preview?: Omit<DragPreview, "x" | "y" | "phase">;
  } | null>(null);
  const chatDraggedRef = useRef(false);
  const chatStackRef = useRef<HTMLDivElement>(null);
  const chatListRef = useRef<HTMLDivElement>(null);
  const closeDropRef = useRef<HTMLDivElement>(null);
  const closeAnimationTimerRef = useRef<number | null>(null);
  const dragPreviewTimerRef = useRef<number | null>(null);
  const chatWindowAnimationTimerRef = useRef<number | null>(null);
  const chatWindowAnimationFrameRef = useRef<number | null>(null);
  const exitingChatWindowKeyRef = useRef<string | null>(null);
  const skipNextAutoAlignRef = useRef(false);

  const { data: conversations = [], isLoading } = useChatConversations(enabled);
  const { data: groups = [], isLoading: isGroupsLoading } = useMyGroups(enabled);
  const markRead = useMarkChatRead();
  const markGroupRead = useMarkGroupRead();

  openChatsRef.current = openChats;
  activeChatKeyRef.current = activeChatKey;

  const trimmedKeyword = keyword.trim();
  const { data: searchResult, isFetching: isSearching } = useQuery({
    queryKey: ["chat", "employee-search", trimmedKeyword],
    queryFn: () => employeeService.search(trimmedKeyword, 0, 10),
    enabled: enabled && panelOpen && trimmedKeyword.length >= 2,
    staleTime: 60_000,
  });

  const clearChatWindowAnimation = useCallback(() => {
    if (chatWindowAnimationTimerRef.current) {
      window.clearTimeout(chatWindowAnimationTimerRef.current);
      chatWindowAnimationTimerRef.current = null;
    }
    if (chatWindowAnimationFrameRef.current) {
      window.cancelAnimationFrame(chatWindowAnimationFrameRef.current);
      chatWindowAnimationFrameRef.current = null;
    }
  }, []);

  const clearLocalUnread = useCallback((key: string) => {
    setLocalUnreadByChatKey((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const incrementLocalUnread = useCallback((key: string) => {
    setLocalUnreadByChatKey((prev) => ({ ...prev, [key]: (prev[key] ?? 0) + 1 }));
  }, []);

  const animateChatWindowIn = useCallback(
    (chat: OpenChat) => {
      const key = chatKey(chat);
      clearChatWindowAnimation();
      exitingChatWindowKeyRef.current = null;
      setChatWindowMotion((prev) => {
        if (prev?.key === key && prev.phase !== "exiting") return { ...prev, chat: makeVisibleChat(chat) };
        return { key, chat: makeVisibleChat(chat), phase: "entering" };
      });
      chatWindowAnimationFrameRef.current = window.requestAnimationFrame(() => {
        chatWindowAnimationFrameRef.current = null;
        setChatWindowMotion((prev) => (prev?.key === key && prev.phase === "entering" ? { ...prev, phase: "open" } : prev));
      });
    },
    [clearChatWindowAnimation],
  );

  const animateChatWindowOut = useCallback(
    (key: string, afterExit: () => void) => {
      const chat = openChatsRef.current.find((item) => chatKey(item) === key);
      if (!chat) {
        afterExit();
        return;
      }

      clearChatWindowAnimation();
      exitingChatWindowKeyRef.current = key;
      setChatWindowMotion({ key, chat: makeVisibleChat(chat), phase: "exiting" });
      chatWindowAnimationTimerRef.current = window.setTimeout(() => {
        afterExit();
        setChatWindowMotion((prev) => (prev?.key === key && prev.phase === "exiting" ? null : prev));
        exitingChatWindowKeyRef.current = null;
        chatWindowAnimationTimerRef.current = null;
      }, CHAT_WINDOW_ANIMATION_MS);
    },
    [clearChatWindowAnimation],
  );

  const bumpChatToFront = (key: string, fallbackChat?: OpenChat, minimized?: boolean, activate = false) => {
    setOpenChats((prev) => {
      const existing = prev.find((chat) => chatKey(chat) === key);
      const nextChat = existing ?? fallbackChat;
      if (!nextChat) return prev;
      const movedChat = typeof minimized === "boolean" ? { ...nextChat, minimized } : nextChat;
      return [...prev.filter((chat) => chatKey(chat) !== key), movedChat];
    });
    if (activate) setActiveChatKey(key);
  };

  useChatSocket(enabled, (message) => {
    const key = `d:${message.senderUserId}`;
    const chat = openChatsRef.current.find(
      (c): c is Extract<OpenChat, { kind: "direct" }> => c.kind === "direct" && c.partnerUserId === message.senderUserId,
    );
    const conversation = conversations.find((c) => c.partnerUserId === message.senderUserId);
    const isVisibleChat = !!chat && !chat.minimized && activeChatKeyRef.current === key;
    const hasOpenWindow = openChatsRef.current.some((c) => !c.minimized);

    if (!isVisibleChat) {
      incrementLocalUnread(key);
      bumpChatToFront(
        key,
        {
          kind: "direct",
          minimized: true,
          partnerUserId: message.senderUserId,
          partnerName: chat?.partnerName ?? conversation?.partnerName,
        },
        true,
        !hasOpenWindow,
      );
    }

    if (isVisibleChat) {
      clearLocalUnread(key);
      markRead.mutate(message.senderUserId);
    }
  });

  useChatGroupSocket(
    enabled,
    (message) => {
      const key = `g:${message.groupId}`;
      const chat = openChatsRef.current.find((c): c is Extract<OpenChat, { kind: "group" }> => c.kind === "group" && c.groupId === message.groupId);
      const group = groups.find((g) => g.id === message.groupId);
      const isVisibleChat = !!chat && !chat.minimized && activeChatKeyRef.current === key;
      const hasOpenWindow = openChatsRef.current.some((c) => !c.minimized);

      if (!isVisibleChat) {
        incrementLocalUnread(key);
        bumpChatToFront(
          key,
          {
            kind: "group",
            minimized: true,
            groupId: message.groupId,
            groupName: chat?.groupName ?? group?.name,
            avatarUrl: chat?.avatarUrl ?? group?.avatarUrl,
          },
          true,
          !hasOpenWindow,
        );
      }

      if (isVisibleChat) {
        clearLocalUnread(key);
        markGroupRead.mutate(message.groupId);
      }
    },
    (groupId) => closeChat(`g:${groupId}`),
  );

  usePresenceSocket(enabled);
  const onlineUsers = useOnlineUsers(enabled);

  useEffect(() => {
    const onResize = () => {
      setChatStackPos((pos) => {
        if (!pos) return pos;
        const rect = chatStackRef.current?.getBoundingClientRect();
        const next = pos.edge
          ? alignChatStackToEdge(pos, pos.edge, rect?.width, rect?.height)
          : snapChatStackPos(pos.x, pos.y, rect?.width, rect?.height);
        return samePos(pos, next) ? pos : next;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    try {
      if (chatStackPos) localStorage.setItem(CHAT_STACK_POS_KEY, JSON.stringify(chatStackPos));
    } catch {}
  }, [chatStackPos]);

  useEffect(() => {
    if (!userId || hydratedUserRef.current === userId) return;
    const state = loadFloatingChatState(userId);
    setOpenChats(state.openChats);
    setActiveChatKey(state.activeChatKey);
    hydratedUserRef.current = userId;
  }, [userId]);

  useEffect(() => {
    if (!userId || hydratedUserRef.current !== userId) return;
    try {
      localStorage.setItem(getFloatingChatStateKey(userId), JSON.stringify(normalizeFloatingChatState({ activeChatKey, openChats })));
    } catch {}
  }, [activeChatKey, openChats, userId]);

  useEffect(() => {
    const openDock = () => {
      setPanelOpen(true);
    };

    window.addEventListener(CHAT_DOCK_OPEN_EVENT, openDock);
    return () => window.removeEventListener(CHAT_DOCK_OPEN_EVENT, openDock);
  }, []);

  const isOverCloseDrop = (clientX: number, clientY: number) => {
    const rect = closeDropRef.current?.getBoundingClientRect();
    if (!rect) return clientY >= window.innerHeight - 120 && Math.abs(clientX - window.innerWidth / 2) <= 96;
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  };

  const onChatBubblePointerDown = (e: PointerEvent<HTMLElement>, key: string, closeAllOnDrop = false) => {
    e.stopPropagation();
    const stackRect = chatStackRef.current?.getBoundingClientRect();
    if (!stackRect) return;
    if (dragPreviewTimerRef.current) {
      window.clearTimeout(dragPreviewTimerRef.current);
      dragPreviewTimerRef.current = null;
    }
    const chat = openChats.find((c) => chatKey(c) === key);
    if (!chat) return;
    const bubbleRect = e.currentTarget.getBoundingClientRect();
    const mode: DragMode = closeAllOnDrop ? "stack" : "avatar";
    const wasOpenOnDrag = !!chat && activeChatKey === key && !chat.minimized;
    chatDragRef.current = {
      key,
      mode,
      startX: e.clientX,
      startY: e.clientY,
      originX: stackRect.left,
      originY: stackRect.top,
      moved: false,
      closeAllOnDrop,
      wasOpenOnDrag,
      savedStackPos: chatStackPos ? { ...chatStackPos } : snapChatStackPos(stackRect.left, stackRect.top, stackRect.width, stackRect.height),
    };
    const name =
      chat.kind === "direct"
        ? getProfileName(profileByUserId.get(chat.partnerUserId), chat.partnerName || chat.partnerUserId)
        : chat.groupName || "Nhóm";
    const src = chat.kind === "direct" ? profileByUserId.get(chat.partnerUserId)?.avatarId : (chat.avatarUrl ?? undefined);
    chatDragRef.current.preview = {
      key,
      returnX: bubbleRect.left + bubbleRect.width / 2,
      returnY: bubbleRect.top + bubbleRect.height / 2,
      name,
      src,
      kind: chat.kind,
      unread: unreadByChatKey.get(key) ?? 0,
      online: chat.kind === "direct" && onlineUsers.has(chat.partnerUserId),
    };
    setDraggingChatKey(key);
    setDragStarted(false);
    setDragOverClose(false);
  };

  const openWindow = useCallback(
    (chat: OpenChat) => {
      const key = chatKey(chat);
      clearChatWindowAnimation();
      exitingChatWindowKeyRef.current = null;
      setPanelOpen(false);
      setKeyword("");
      setChatStackPos((pos) => pos ?? getDefaultChatStackPos());
      clearLocalUnread(key);
      setActiveChatKey(key);
      setOpenChats((prev) => {
        const existing = prev.find((c) => chatKey(c) === key);
        if (existing) {
          return prev.map((c) => (chatKey(c) === key ? { ...chat, minimized: false } : c));
        }
        return [...prev, chat];
      });
    },
    [clearChatWindowAnimation, clearLocalUnread],
  );

  const openDirect = useCallback(
    (partnerUserId: string, partnerName?: string) => openWindow({ kind: "direct", minimized: false, partnerUserId, partnerName }),
    [openWindow],
  );

  const openGroup = useCallback(
    (groupId: string, groupName?: string, avatarUrl?: string | null) =>
      openWindow({ kind: "group", minimized: false, groupId, groupName, avatarUrl }),
    [openWindow],
  );

  const closeChat = (key: string, openNext = false) => {
    const prev = openChatsRef.current;
    const shouldOpenNext = openNext || prev.some((chat) => !chat.minimized);
    const filtered = prev.filter((c) => chatKey(c) !== key);

    if (filtered.length === 0) {
      openChatsRef.current = [];
      activeChatKeyRef.current = null;
      setChatWindowMotion(null);
      clearChatWindowAnimation();
      exitingChatWindowKeyRef.current = null;
      setOpenChats([]);
      setActiveChatKey(null);
      setPanelOpen(false);
      return;
    }

    const next = shouldOpenNext ? filtered.map((c, index) => (index === filtered.length - 1 ? { ...c, minimized: false } : c)) : filtered;
    const nextActiveKey = shouldOpenNext ? chatKey(next[next.length - 1]) : activeChatKeyRef.current === key ? null : activeChatKeyRef.current;

    openChatsRef.current = next;
    activeChatKeyRef.current = nextActiveKey;
    setOpenChats(next);
    setActiveChatKey(nextActiveKey);
  };

  const closeAllChats = () => {
    setChatWindowMotion(null);
    clearChatWindowAnimation();
    exitingChatWindowKeyRef.current = null;
    setActiveChatKey(null);
    setOpenChats([]);
    setPanelOpen(false);
  };

  const animateCloseDrop = (drag: NonNullable<typeof chatDragRef.current>) => {
    if (closeAnimationTimerRef.current) window.clearTimeout(closeAnimationTimerRef.current);
    setClosingDrop({ key: drag.key, all: !!drag.closeAllOnDrop });
    if (drag.closeAllOnDrop && drag.savedStackPos) {
      skipNextAutoAlignRef.current = true;
      setChatStackPos(drag.savedStackPos);
      try {
        localStorage.setItem(CHAT_STACK_POS_KEY, JSON.stringify(drag.savedStackPos));
      } catch {}
    }
    if (drag.wasOpenOnDrag) {
      skipNextAutoAlignRef.current = true;
    }
    if (drag.mode === "avatar" && !drag.closeAllOnDrop) {
      const closeRect = closeDropRef.current?.getBoundingClientRect();
      const x = closeRect ? closeRect.left + closeRect.width / 2 : drag.startX;
      const y = closeRect ? closeRect.top + closeRect.height / 2 : drag.startY;
      setDragPreview((preview) => (preview ? { ...preview, x, y, phase: "closing" } : preview));
    }
    closeAnimationTimerRef.current = window.setTimeout(() => {
      if (drag.closeAllOnDrop) {
        closeAllChats();
      } else {
        closeChat(drag.key, drag.wasOpenOnDrag);
      }
      setClosingDrop(null);
      setDragPreview(null);
      closeAnimationTimerRef.current = null;
    }, CLOSE_ANIMATION_MS);
  };

  useEffect(
    () => () => {
      if (closeAnimationTimerRef.current) window.clearTimeout(closeAnimationTimerRef.current);
      if (dragPreviewTimerRef.current) window.clearTimeout(dragPreviewTimerRef.current);
      clearChatWindowAnimation();
    },
    [clearChatWindowAnimation],
  );

  useEffect(() => {
    if (!draggingChatKey) return;

    const onPointerMove = (event: globalThis.PointerEvent) => {
      const drag = chatDragRef.current;
      if (!drag) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) < 6) return;
      if (!drag.moved) {
        drag.moved = true;
        setDragStarted(true);
        setPanelOpen(false);
        if (drag.mode === "avatar" && drag.preview) {
          setDragPreview({
            ...drag.preview,
            x: event.clientX,
            y: event.clientY,
            phase: "dragging",
          });
          if (drag.wasOpenOnDrag) {
            skipNextAutoAlignRef.current = true;
            setOpenChats((prev) => prev.map((c) => (chatKey(c) === drag.key ? { ...c, minimized: true } : c)));
          }
        }
      }
      if (drag.mode === "stack") {
        const rect = chatStackRef.current?.getBoundingClientRect();
        setChatStackPos(clampChatStackPos(drag.originX + dx, drag.originY + dy, rect?.width, rect?.height));
      } else {
        setDragPreview((preview) => (preview ? { ...preview, x: event.clientX, y: event.clientY, phase: "dragging" } : preview));
      }
      setDragOverClose(isOverCloseDrop(event.clientX, event.clientY));
    };

    const onPointerUp = (event: globalThis.PointerEvent) => {
      const drag = chatDragRef.current;
      if (!drag) return;
      const droppedOnClose = drag.moved && isOverCloseDrop(event.clientX, event.clientY);
      if (droppedOnClose) {
        animateCloseDrop(drag);
      } else if (drag.mode === "stack" && drag.moved && !drag.wasOpenOnDrag) {
        const rect = chatStackRef.current?.getBoundingClientRect();
        setChatStackPos((pos) => {
          if (!pos) return pos;
          const next = snapChatStackPos(pos.x, pos.y, rect?.width, rect?.height);
          return samePos(pos, next) ? pos : next;
        });
      } else if (drag.mode === "avatar" && drag.moved) {
        setDragPreview((preview) => (preview ? { ...preview, x: preview.returnX, y: preview.returnY, phase: "returning" } : preview));
        dragPreviewTimerRef.current = window.setTimeout(() => {
          setDragPreview(null);
          dragPreviewTimerRef.current = null;
        }, 160);
      }
      if (drag.wasOpenOnDrag && drag.moved && !droppedOnClose) {
        skipNextAutoAlignRef.current = true;
        setActiveChatKey(drag.key);
        setOpenChats((prev) => prev.map((c) => (chatKey(c) === drag.key ? { ...c, minimized: false } : c)));
      }
      if (drag.moved) chatDraggedRef.current = true;
      chatDragRef.current = null;
      setDraggingChatKey(null);
      setDragStarted(false);
      setDragOverClose(false);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draggingChatKey]);

  const toggleMinimize = (key: string) => {
    const chat = openChatsRef.current.find((c) => chatKey(c) === key);
    if (!chat) return;

    if (!chat.minimized && activeChatKeyRef.current === key) {
      setPanelOpen(false);
      animateChatWindowOut(key, () => {
        setOpenChats((prev) => {
          const current = prev.find((c) => chatKey(c) === key);
          if (!current) return prev;
          return prev.map((c) => (chatKey(c) === key ? { ...current, minimized: true } : c));
        });
        setActiveChatKey(key);
      });
      return;
    }

    clearChatWindowAnimation();
    exitingChatWindowKeyRef.current = null;
    setPanelOpen(false);
    clearLocalUnread(key);
    setOpenChats((prev) => {
      const current = prev.find((c) => chatKey(c) === key);
      if (!current) return prev;
      return prev.map((c) => (chatKey(c) === key ? { ...current, minimized: !current.minimized } : c));
    });
    setActiveChatKey(key);
  };

  const activateChat = (key: string) => {
    clearChatWindowAnimation();
    exitingChatWindowKeyRef.current = null;
    setPanelOpen(false);
    setKeyword("");
    clearLocalUnread(key);
    setActiveChatKey(key);
    setOpenChats((prev) => {
      const chat = prev.find((c) => chatKey(c) === key);
      if (!chat) return prev;
      return prev.map((c) => (chatKey(c) === key ? { ...chat, minimized: false } : c));
    });
  };

  useEffect(() => {
    const onOpenConversation = (event: Event) => {
      const detail = (event as CustomEvent<ChatConversationOpenDetail>).detail;
      if (!detail?.partnerUserId) return;
      openDirect(detail.partnerUserId, detail.partnerName);
    };

    const onOpenGroup = (event: Event) => {
      const detail = (event as CustomEvent<ChatGroupOpenDetail>).detail;
      if (!detail?.groupId) return;
      openGroup(detail.groupId, detail.groupName, detail.avatarUrl);
    };

    window.addEventListener(CHAT_CONVERSATION_OPEN_EVENT, onOpenConversation);
    window.addEventListener(CHAT_GROUP_OPEN_EVENT, onOpenGroup);
    return () => {
      window.removeEventListener(CHAT_CONVERSATION_OPEN_EVENT, onOpenConversation);
      window.removeEventListener(CHAT_GROUP_OPEN_EVENT, onOpenGroup);
    };
  }, [openDirect, openGroup]);

  useEffect(() => {
    const onConversationRead = (event: Event) => {
      const detail = (event as CustomEvent<ChatConversationReadDetail>).detail;
      if (detail?.partnerUserId) clearLocalUnread(`d:${detail.partnerUserId}`);
    };

    const onGroupRead = (event: Event) => {
      const detail = (event as CustomEvent<ChatGroupReadDetail>).detail;
      if (detail?.groupId) clearLocalUnread(`g:${detail.groupId}`);
    };

    window.addEventListener(CHAT_CONVERSATION_READ_EVENT, onConversationRead);
    window.addEventListener(CHAT_GROUP_READ_EVENT, onGroupRead);
    return () => {
      window.removeEventListener(CHAT_CONVERSATION_READ_EVENT, onConversationRead);
      window.removeEventListener(CHAT_GROUP_READ_EVENT, onGroupRead);
    };
  }, [clearLocalUnread]);

  const directPartnerIds = useMemo(() => {
    const ids = new Set<string>();
    conversations.forEach((c) => ids.add(c.partnerUserId));
    openChats.forEach((c) => {
      if (c.kind === "direct") ids.add(c.partnerUserId);
    });
    return Array.from(ids);
  }, [conversations, openChats]);
  const profileByUserId = useChatUserProfiles(directPartnerIds);

  const unified: UnifiedItem[] = useMemo(() => {
    const items: UnifiedItem[] = [];
    conversations.forEach((c) => items.push({ key: `d:${c.partnerUserId}`, kind: "direct", sortMs: c.lastMessageEpochMs ?? 0, conv: c }));
    groups.forEach((g) => items.push({ key: `g:${g.id}`, kind: "group", sortMs: g.lastMessageEpochMs ?? 0, group: g }));
    return items.sort((a, b) => b.sortMs - a.sortMs);
  }, [conversations, groups]);
  useEffect(() => {
    if (!panelOpen) return;
    const frame = window.requestAnimationFrame(() => {
      chatListRef.current?.scrollTo({ top: 0, behavior: "auto" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [panelOpen, trimmedKeyword, unified.length]);

  const unreadByChatKey = useMemo(() => {
    const map = new Map<string, number>();
    conversations.forEach((c) => map.set(`d:${c.partnerUserId}`, c.unreadCount));
    groups.forEach((g) => map.set(`g:${g.id}`, g.unreadCount));
    Object.entries(localUnreadByChatKey).forEach(([key, count]) => {
      map.set(key, Math.max(map.get(key) ?? 0, count));
    });
    return map;
  }, [conversations, groups, localUnreadByChatKey]);

  const chatLayoutKey = useMemo(() => openChats.map((c) => `${chatKey(c)}:${c.minimized ? "1" : "0"}`).join("|"), [openChats]);
  const visibleChats = openChats;
  const selectedChat = activeChatKey ? visibleChats.find((c) => chatKey(c) === activeChatKey) : undefined;
  const activeChat = selectedChat && !selectedChat.minimized ? selectedChat : undefined;
  const activeChatRenderKey = activeChat ? chatKey(activeChat) : null;

  useEffect(() => {
    if (!activeChat || !activeChatRenderKey || exitingChatWindowKeyRef.current === activeChatRenderKey) return;
    animateChatWindowIn(activeChat);
  }, [activeChat, activeChatRenderKey, animateChatWindowIn]);

  useEffect(() => {
    if (openChats.length === 0) return;
    const frame = requestAnimationFrame(() => {
      if (draggingChatKey || skipNextAutoAlignRef.current) {
        skipNextAutoAlignRef.current = false;
        return;
      }
      const selected = activeChatKey ? openChats.find((c) => chatKey(c) === activeChatKey) : undefined;
      const hasOpenWindow = !!selected && !selected.minimized;
      const rect = chatStackRef.current?.getBoundingClientRect();
      const fallbackWidth = hasOpenWindow ? CHAT_STACK_WIDTH : COLLAPSED_STACK_SIZE;
      const fallbackHeight = hasOpenWindow ? CHAT_STACK_HEIGHT : COLLAPSED_STACK_SIZE;

      setChatStackPos((pos) => {
        const base = pos ?? getDefaultChatStackPos();
        const width = rect?.width || fallbackWidth;
        const height = rect?.height || fallbackHeight;
        const next = base.edge ? alignChatStackToEdge(base, base.edge, width, height) : snapChatStackPos(base.x, base.y, width, height);
        return samePos(pos, next) ? pos : next;
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeChatKey, chatLayoutKey, draggingChatKey, openChats]);

  if (!enabled || typeof document === "undefined") return null;

  const renderedChat = chatWindowMotion?.chat ?? activeChat;
  const renderedChatKey = renderedChat ? chatKey(renderedChat) : null;
  const renderedChatPhase = chatWindowMotion?.key === renderedChatKey ? chatWindowMotion.phase : "entering";
  const defaultBubbleChats = visibleChats.slice(-MAX_VISIBLE_CHAT_BUBBLES);
  const activeVisibleChat = activeChatKey ? visibleChats.find((chat) => chatKey(chat) === activeChatKey) : undefined;
  const bubbleChats =
    activeVisibleChat && !defaultBubbleChats.some((chat) => chatKey(chat) === activeChatKey)
      ? [activeVisibleChat, ...defaultBubbleChats.slice(-(MAX_VISIBLE_CHAT_BUBBLES - 1))]
      : defaultBubbleChats;
  const bubbleChatKeys = new Set(bubbleChats.map((chat) => chatKey(chat)));
  const hiddenChats = visibleChats.filter((chat) => !bubbleChatKeys.has(chatKey(chat)));
  const hiddenChatCount = hiddenChats.length;
  const hiddenChatsPreview = hiddenChats.slice(-1);
  const hiddenChatPreviewLoading = hiddenChatsPreview.some((chat) => chat.kind === "direct" && !profileByUserId.has(chat.partnerUserId));
  const chatStackOpen = visibleChats.length > 0;
  const employees = (searchResult?.content ?? []).filter((e) => e.userId && e.userId !== userId);
  const showSearch = trimmedKeyword.length >= 2;
  const listLoading = isLoading || isGroupsLoading;

  const renderDirect = (c: ChatConversationDto) => (
    <div
      onClick={() => openDirect(c.partnerUserId, c.partnerName)}
      onContextMenu={(event) => {
        event.preventDefault();
        openDirect(c.partnerUserId, c.partnerName);
      }}
      className={`w-full flex gap-3 p-2.5 rounded-xl cursor-pointer transition-all hover:shadow-sm ${
        c.unreadCount > 0 ? "bg-blue-50 hover:bg-blue-100" : "bg-white hover:bg-neutral-100"
      }`}
    >
      <div className="relative shrink-0">
        <CommonBadge count={c.unreadCount} size="small" overflowCount={9}>
          <CommonAvatar
            size={40}
            src={profileByUserId.get(c.partnerUserId)?.avatarId}
            name={getProfileName(profileByUserId.get(c.partnerUserId), c.partnerName)}
            className="bg-blue-500 text-white"
          />
        </CommonBadge>
        {onlineUsers.has(c.partnerUserId) && <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between gap-2">
          <Typography.Text ellipsis className="font-semibold text-sm text-neutral-800">
            {getProfileName(profileByUserId.get(c.partnerUserId), c.partnerName || c.partnerUserId)}
          </Typography.Text>
          <span className="text-[11px] text-neutral-400 shrink-0">{c.lastMessageTime}</span>
        </div>
        <div className={`text-xs mt-0.5 truncate ${c.unreadCount > 0 ? "font-medium text-neutral-800" : "text-neutral-500"}`}>
          {c.lastMessageFromMe ? "Bạn: " : ""}
          {c.lastMessageType === "STICKER" ? `${getStickerEmoji(c.lastMessageContent) ?? ""} Nhãn dán`.trim() : c.lastMessageContent}
        </div>
      </div>
    </div>
  );

  const renderGroup = (g: ChatGroupListDto) => {
    const senderPrefix = g.lastMessageSenderName ? `${g.lastMessageSenderName}: ` : "";
    const preview =
      g.lastMessageContent == null
        ? "Chưa có tin nhắn"
        : g.lastMessageType === "SYSTEM"
          ? g.lastMessageContent
          : g.lastMessageType === "STICKER"
            ? `${senderPrefix}${getStickerEmoji(g.lastMessageContent) ?? ""} Nhãn dán`.trim()
            : `${senderPrefix}${g.lastMessageContent}`;
    return (
      <div
        onClick={() => openGroup(g.id, g.name, g.avatarUrl)}
        onContextMenu={(event) => {
          event.preventDefault();
          openGroup(g.id, g.name, g.avatarUrl);
        }}
        className={`w-full flex gap-3 p-2.5 rounded-xl cursor-pointer transition-all hover:shadow-sm ${
          g.unreadCount > 0 ? "bg-blue-50 hover:bg-blue-100" : "bg-white hover:bg-neutral-100"
        }`}
      >
        <div className="relative shrink-0">
          <CommonBadge count={g.unreadCount} size="small" overflowCount={9}>
            <CommonAvatar size={40} src={g.avatarUrl ?? undefined} name={g.name} className="bg-[#0a2240] text-white" />
          </CommonBadge>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex justify-between gap-2">
            <Typography.Text ellipsis className="font-semibold text-sm text-neutral-800">
              {g.name} <span className="font-normal text-neutral-400">({g.memberCount})</span>
            </Typography.Text>
            <span className="text-[11px] text-neutral-400 shrink-0">{g.lastMessageTime}</span>
          </div>
          <div className={`text-xs mt-0.5 truncate ${g.unreadCount > 0 ? "font-medium text-neutral-800" : "text-neutral-500"}`}>{preview}</div>
        </div>
      </div>
    );
  };

  const panel = (
    <div className="w-[340px] max-w-[90vw] overflow-hidden rounded-[18px] border border-[#e5edf7] bg-white shadow-[0_18px_48px_rgba(15,23,42,0.22)]">
      <div className="px-3 py-2 border-b">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold text-neutral-800">Tin nhắn</div>
          <Button size="small" type="text" icon={<UsergroupAddOutlined />} className="text-blue-600" onClick={() => setCreateOpen(true)}>
            Tạo nhóm
          </Button>
        </div>
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined className="text-neutral-400" />}
          placeholder="Tìm nhân viên để chat..."
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
        />
      </div>

      <div ref={chatListRef} className="bg-neutral-50 p-2 max-h-[400px] overflow-y-auto">
        {showSearch ? (
          isSearching ? (
            <div className="flex justify-center py-8">
              <Spin />
            </div>
          ) : employees.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không tìm thấy nhân viên" className="py-6" />
          ) : (
            <List
              split={false}
              dataSource={employees}
              renderItem={(e) => (
                <List.Item className="!p-0 mb-1">
                  <div
                    onClick={() => openDirect(e.userId, e.fullName)}
                    onContextMenu={(event) => {
                      event.preventDefault();
                      openDirect(e.userId, e.fullName);
                    }}
                    className="w-full flex gap-3 p-2.5 rounded-xl cursor-pointer bg-white hover:bg-neutral-100 transition-all"
                  >
                    <div className="relative shrink-0">
                      <CommonAvatar size={40} src={e.avatarId} name={e.fullName} className="bg-blue-500 text-white" />
                      {onlineUsers.has(e.userId) && (
                        <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Typography.Text ellipsis className="font-semibold text-sm text-neutral-800 block">
                        {e.fullName}
                      </Typography.Text>
                      <div className="text-xs text-neutral-500 truncate">{[e.employeeCode, e.jobTitle].filter(Boolean).join(" - ")}</div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )
        ) : listLoading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : unified.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có hội thoại, tìm nhân viên hoặc tạo nhóm để bắt đầu" className="py-6" />
        ) : (
          <div className="flex flex-col gap-1">
            {unified.map((item) => (
              <div key={item.key}>{item.kind === "direct" ? renderDirect(item.conv) : renderGroup(item.group)}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const openChatsPanel = (
    <div className="w-[300px] max-w-[88vw] overflow-hidden rounded-[16px] bg-white shadow-[0_18px_48px_rgba(15,23,42,0.22)]">
      <div className="border-b border-[#eef1f5] px-3.5 py-2.5">
        <div className="text-sm font-semibold text-[#111827]">Chat đang mở</div>
        <div className="text-[11px] text-neutral-400">{visibleChats.length} đoạn chat</div>
        {hiddenChatPreviewLoading && (
          <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-400">
            <Spin size="small" />
            <span>Đang tải danh sách chat</span>
          </div>
        )}
      </div>
      <div className="max-h-[360px] overflow-y-auto bg-neutral-50 p-2">
        {visibleChats
          .slice()
          .reverse()
          .map((chat) => {
            const key = chatKey(chat);
            const active = activeChatKey === key;
            const name =
              chat.kind === "direct"
                ? getProfileName(profileByUserId.get(chat.partnerUserId), chat.partnerName || chat.partnerUserId)
                : chat.groupName || "Nhóm";
            const src = chat.kind === "direct" ? profileByUserId.get(chat.partnerUserId)?.avatarId : (chat.avatarUrl ?? undefined);
            const unread = unreadByChatKey.get(key) ?? 0;

            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setPanelOpen(false);
                  activateChat(key);
                }}
                className={[
                  "mb-1 flex w-full cursor-pointer items-center gap-2 rounded-xl border-none p-2 text-left transition",
                  active ? "bg-blue-50" : "bg-white hover:bg-neutral-100",
                ].join(" ")}
              >
                <div className="relative shrink-0">
                  <CommonAvatar
                    size={34}
                    src={src}
                    name={name}
                    className={chat.kind === "direct" ? "bg-blue-500 text-white" : "bg-[#0a2240] text-white"}
                  />
                  {chat.kind === "direct" && onlineUsers.has(chat.partnerUserId) && (
                    <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-green-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <Typography.Text ellipsis className="font-semibold text-sm text-neutral-800">
                      {name}
                    </Typography.Text>
                    {active && <span className="shrink-0 rounded-full bg-[#0068ff] px-1.5 py-0.5 text-[10px] font-medium text-white">Đang xem</span>}
                  </div>
                  <div className="truncate text-[11px] text-neutral-500">{chat.kind === "direct" ? "Chat riêng" : "Nhóm chat"}</div>
                </div>
                {unread > 0 && (
                  <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-[#ff4d4f] px-1 text-[11px] font-semibold text-white">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
            );
          })}
      </div>
    </div>
  );

  const renderChatConversationWindow = (c: OpenChat) => {
    const key = chatKey(c);
    const onToggleMinimize = () => {
      if (chatDraggedRef.current) {
        chatDraggedRef.current = false;
        return;
      }
      toggleMinimize(key);
    };

    return c.kind === "direct" ? (
      <DirectChatWindow
        key={key}
        partnerUserId={c.partnerUserId}
        partnerName={c.partnerName}
        minimized={c.minimized}
        onToggleMinimize={onToggleMinimize}
      />
    ) : (
      <GroupChatWindow
        key={key}
        groupId={c.groupId}
        groupName={c.groupName}
        avatarUrl={c.avatarUrl}
        minimized={c.minimized}
        onClose={() => closeChat(key)}
        onToggleMinimize={onToggleMinimize}
      />
    );
  };

  const renderChatBubble = (c: OpenChat, draggable = true) => {
    const key = chatKey(c);
    const active = renderedChatKey === key;
    const name = c.kind === "direct" ? getProfileName(profileByUserId.get(c.partnerUserId), c.partnerName || c.partnerUserId) : c.groupName || "Nhóm";
    const src = c.kind === "direct" ? profileByUserId.get(c.partnerUserId)?.avatarId : (c.avatarUrl ?? undefined);
    const unread = unreadByChatKey.get(key) ?? 0;

    return (
      <button
        key={key}
        type="button"
        title={name}
        draggable={false}
        style={{ touchAction: "none" }}
        onDragStart={(e) => e.preventDefault()}
        onPointerDown={
          draggable
            ? (e) => onChatBubblePointerDown(e, key)
            : (e) => {
                e.stopPropagation();
                chatDraggedRef.current = false;
              }
        }
        onClick={(e) => {
          e.stopPropagation();
          if (!draggable) {
            chatDraggedRef.current = false;
            if (active && !c.minimized) {
              toggleMinimize(key);
              return;
            }
            activateChat(key);
            return;
          }
          if (chatDraggedRef.current) {
            chatDraggedRef.current = false;
            return;
          }
          if (active && !c.minimized) {
            toggleMinimize(key);
            return;
          }
          activateChat(key);
        }}
        className={[
          "relative h-12 w-12 select-none rounded-full border-2 bg-white p-0 shadow-[0_8px_22px_rgba(15,23,42,0.18)] transition-[transform,opacity,filter,border-color] duration-200 ease-out transform-gpu will-change-transform hover:-translate-y-0.5 [&_*]:pointer-events-none",
          draggable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer",
          active ? "border-[#0068ff]" : "border-white",
          dragPreview?.key === key ? "pointer-events-none scale-75 opacity-0" : draggingChatKey === key && dragStarted ? "scale-105 opacity-90" : "",
          closingDrop?.key === key && !closingDrop.all ? "translate-y-8 scale-50 opacity-0 blur-[1px]" : "",
        ].join(" ")}
      >
        <CommonAvatar size={44} src={src} name={name} className={c.kind === "direct" ? "bg-blue-500 text-white" : "bg-[#0a2240] text-white"} />
        {c.kind === "direct" && onlineUsers.has(c.partnerUserId) && (
          <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-green-500" />
        )}
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-[#ff4d4f] px-1 text-[11px] font-semibold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        {active && (
          <span className="pointer-events-none absolute left-1/2 top-[calc(100%+5px)] h-0 w-0 -translate-x-1/2 border-x-[8px] border-t-[9px] border-x-transparent border-t-[#0068ff] drop-shadow-[0_2px_2px_rgba(15,23,42,0.18)]" />
        )}
      </button>
    );
  };

  const renderCollapsedChatStack = (chats: OpenChat[]) => {
    const topChat = (activeChatKey ? chats.find((chat) => chatKey(chat) === activeChatKey) : undefined) ?? chats[chats.length - 1];
    if (!topChat) return null;

    const key = chatKey(topChat);
    const name =
      topChat.kind === "direct"
        ? getProfileName(profileByUserId.get(topChat.partnerUserId), topChat.partnerName || topChat.partnerUserId)
        : topChat.groupName || "Nhóm";
    const src = topChat.kind === "direct" ? profileByUserId.get(topChat.partnerUserId)?.avatarId : (topChat.avatarUrl ?? undefined);
    const unread = unreadByChatKey.get(key) ?? 0;

    return (
      <button
        type="button"
        title={name}
        draggable={false}
        style={{ touchAction: "none" }}
        onDragStart={(e) => e.preventDefault()}
        onPointerDown={(e) => onChatBubblePointerDown(e, key, true)}
        onClick={(e) => {
          e.stopPropagation();
          if (chatDraggedRef.current) {
            chatDraggedRef.current = false;
            return;
          }
          activateChat(key);
        }}
        className={[
          "relative flex h-14 w-14 select-none items-center justify-center rounded-full border-[3px] border-white bg-white p-0 shadow-[0_10px_28px_rgba(15,23,42,0.22)] transition-[transform,opacity,filter] duration-200 ease-out transform-gpu will-change-transform hover:-translate-y-0.5 [&_*]:pointer-events-none",
          dragPreview?.key === key ? "pointer-events-none scale-75 opacity-0" : draggingChatKey === key && dragStarted ? "scale-105 opacity-90" : "",
          closingDrop?.key === key && !closingDrop.all ? "pointer-events-none translate-y-8 scale-50 opacity-0 blur-[1px]" : "",
        ].join(" ")}
      >
        <CommonAvatar size={50} src={src} name={name} className={topChat.kind === "direct" ? "bg-blue-500 text-white" : "bg-[#0a2240] text-white"} />
        <span className="absolute -bottom-1 -left-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-[#0068ff] text-white shadow-sm">
          <MessageOutlined style={{ fontSize: 13 }} />
        </span>
        {topChat.kind === "direct" && onlineUsers.has(topChat.partnerUserId) && (
          <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-green-500" />
        )}
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-[#ff4d4f] px-1 text-[11px] font-semibold text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
    );
  };

  const portalContent = (
    <>
      {chatStackOpen && (
        <div
          ref={chatStackRef}
          className={[
            "fixed z-[1400] flex max-w-[calc(100vw-16px)] origin-bottom flex-col items-center gap-2 transform-gpu",
            draggingChatKey && dragStarted
              ? "transition-none"
              : "transition-[left,top,transform,opacity,filter] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
            closingDrop?.all ? "pointer-events-none translate-y-12 scale-75 opacity-0 blur-[1px]" : "",
          ].join(" ")}
          style={{
            ...(chatStackPos ? { left: chatStackPos.x, top: chatStackPos.y } : { left: getDefaultChatStackPos().x, top: getDefaultChatStackPos().y }),
            willChange: draggingChatKey && dragStarted ? "auto" : "left, top, transform, opacity, filter",
          }}
        >
          {(() => {
            const hasOpenWindow = !!renderedChat;

            return (
              <>
                <div className="flex max-w-full items-center justify-center overflow-visible px-1 py-1 transition-[transform,opacity] duration-200 ease-out transform-gpu">
                  {hasOpenWindow ? (
                    <div className="flex max-w-full items-center justify-center gap-1.5 overflow-visible px-1 pb-2">
                      {bubbleChats.map((c) => renderChatBubble(c))}
                      {hiddenChatCount > 0 && (
                        <Popover
                          content={openChatsPanel}
                          trigger="click"
                          placement="bottom"
                          overlayStyle={{ zIndex: 1420 }}
                          overlayInnerStyle={{
                            padding: 0,
                            borderRadius: 16,
                            overflow: "hidden",
                            boxShadow: "0 18px 48px rgba(15, 23, 42, 0.22)",
                          }}
                        >
                          <button
                            type="button"
                            title="Xem tất cả chat đang mở"
                            onClick={(event) => event.stopPropagation()}
                            className="flex h-12 min-w-12 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-[#111827] px-2 text-sm font-semibold text-white shadow-[0_8px_22px_rgba(15,23,42,0.18)] transition hover:-translate-y-0.5 hover:bg-[#1f2937]"
                          >
                            {hiddenChatPreviewLoading ? (
                              <Spin size="small" />
                            ) : (
                              <div className="flex items-center">
                                {hiddenChatsPreview.map((chat) => {
                                  const key = chatKey(chat);
                                  const src =
                                    chat.kind === "direct" ? profileByUserId.get(chat.partnerUserId)?.avatarId : (chat.avatarUrl ?? undefined);
                                  const name =
                                    chat.kind === "direct"
                                      ? getProfileName(profileByUserId.get(chat.partnerUserId), chat.partnerName || chat.partnerUserId)
                                      : chat.groupName || "Nhóm";
                                  return (
                                    <span key={key} className="relative z-10 flex">
                                      <CommonAvatar
                                        size={26}
                                        src={src}
                                        name={name}
                                        className={chat.kind === "direct" ? "bg-blue-500 text-white" : "bg-[#0a2240] text-white"}
                                      />
                                    </span>
                                  );
                                })}
                                <span className="ml-1.5 text-[12px] font-semibold">+{hiddenChatCount}</span>
                              </div>
                            )}
                          </button>
                        </Popover>
                      )}
                    </div>
                  ) : (
                    renderCollapsedChatStack(visibleChats)
                  )}
                  {hasOpenWindow && (
                    <button
                      type="button"
                      aria-label="Mở danh sách chat"
                      onClick={(event) => {
                        event.stopPropagation();
                        setPanelOpen((open) => !open);
                      }}
                      className="ml-2 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border-2 border-white bg-white text-xl text-[#0068ff] shadow-[0_8px_22px_rgba(15,23,42,0.18)] transition hover:-translate-y-0.5 hover:bg-[#eef4ff] disabled:cursor-default disabled:opacity-90"
                      style={{ zIndex: visibleChats.length + 1 }}
                    >
                      {listLoading ? <Spin size="small" /> : "+"}
                    </button>
                  )}
                </div>
                {hasOpenWindow && panelOpen && <div className="absolute right-0 top-[62px] z-[1430] origin-top-right transform-gpu">{panel}</div>}
                {renderedChat && (
                  <div
                    className={[
                      "shrink-0 origin-top transform-gpu will-change-transform transition-[transform,opacity,filter] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]",
                      renderedChatPhase === "entering" ? "pointer-events-none translate-y-3 scale-[0.96] opacity-0 blur-[1px]" : "",
                      renderedChatPhase === "open" ? "translate-y-0 scale-100 opacity-100 blur-0" : "",
                      renderedChatPhase === "exiting" ? "pointer-events-none -translate-y-2 scale-[0.96] opacity-0 blur-[1px]" : "",
                      closingDrop?.key === chatKey(renderedChat) && !closingDrop.all
                        ? "pointer-events-none translate-y-8 scale-95 opacity-0 blur-[1px]"
                        : "",
                    ].join(" ")}
                  >
                    {renderChatConversationWindow(renderedChat)}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {dragPreview && (
        <div
          className={[
            "fixed z-[1510] h-14 w-14 pointer-events-none rounded-full border-[3px] border-white bg-white p-0 shadow-[0_18px_44px_rgba(15,23,42,0.3)]",
            dragPreview.phase === "dragging"
              ? "transition-transform duration-100 ease-out"
              : "transition-[left,top,transform,opacity,filter] duration-200 ease-out",
          ].join(" ")}
          style={{
            left: dragPreview.x,
            top: dragPreview.y,
            opacity: dragPreview.phase === "closing" ? 0 : 1,
            filter: dragPreview.phase === "closing" ? "blur(1px)" : "none",
            transform: `translate(-50%, -50%) scale(${
              dragPreview.phase === "closing" ? 0.18 : dragPreview.phase === "returning" ? 1 : dragOverClose ? 0.88 : 1.12
            })`,
          }}
        >
          <CommonAvatar
            size={50}
            src={dragPreview.src ?? undefined}
            name={dragPreview.name}
            className={dragPreview.kind === "direct" ? "bg-blue-500 text-white" : "bg-[#0a2240] text-white"}
          />
          <span className="absolute -bottom-1 -left-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-[#0068ff] text-white shadow-sm">
            <MessageOutlined style={{ fontSize: 13 }} />
          </span>
          {dragPreview.online && <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-green-500" />}
          {dragPreview.unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-[#ff4d4f] px-1 text-[11px] font-semibold text-white">
              {dragPreview.unread > 9 ? "9+" : dragPreview.unread}
            </span>
          )}
        </div>
      )}

      {(dragStarted || closingDrop) && (
        <div
          ref={closeDropRef}
          className={[
            "fixed bottom-7 left-1/2 z-[1500] flex h-16 w-16 -translate-x-1/2 items-center justify-center rounded-full border-2 border-white text-2xl text-white shadow-[0_14px_34px_rgba(15,23,42,0.3)] transition",
            dragOverClose || closingDrop ? "scale-110 bg-red-500" : "bg-slate-700/85",
            closingDrop ? "opacity-0 scale-150 duration-200" : "",
          ].join(" ")}
        >
          <CloseOutlined />
        </div>
      )}

      <CreateGroupModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(groupId) => openGroup(groupId)}
      />
    </>
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const location = useLocation();
  if (location.pathname === MESSAGES_PATHS.ROOT) return null;
  return createPortal(portalContent, document.body);
};
